import {
  calculateAutoImageScale,
  loadImageDimensions
} from 'utils/image-scale';
import { loadLevelLayoutImageDimensions } from 'utils/load-level-layout-image-dims';
import {
  ResolveStackedLevelImageTransformsInput,
  StackedLevelImageTransform
} from 'utils/resolve-stacked-level-image-transforms-types';
import {
  computeStackedLayoutImageTransform,
  pickLargestLayoutImage
} from 'utils/stacked-layout-image-transform';

export async function resolveStackedLevelImageTransforms({
  assignments,
  structureAttachments,
  cachedDimsByAttachmentId,
  totalSqft,
  levelCount,
  loadDimensions = loadImageDimensions
}: ResolveStackedLevelImageTransformsInput): Promise<
  StackedLevelImageTransform[]
> {
  const images = await loadLevelLayoutImageDimensions({
    assignments,
    structureAttachments,
    cachedDimsByAttachmentId,
    loadDimensions
  });

  const largest = pickLargestLayoutImage(images);
  if (!largest) {
    return [];
  }

  const inheritedScale = largest.assignment.scale;
  const scale =
    inheritedScale ??
    calculateAutoImageScale(
      largest.dims.naturalWidth,
      largest.dims.naturalHeight,
      totalSqft,
      levelCount
    );

  if (inheritedScale == null && (totalSqft <= 0 || levelCount <= 0)) {
    return [];
  }

  const largestStart = {
    startPositionX: largest.hasPose
      ? largest.assignment.startPositionX ?? 0
      : 0,
    startPositionY: largest.hasPose ? largest.assignment.startPositionY ?? 0 : 0
  };
  const rotation = largest.assignment.rotation ?? 0;

  return images
    .filter((image) => {
      if (image.assignment.scale == null) {
        return true;
      }
      if (image.attachmentId === largest.attachmentId) {
        return false;
      }
      return (
        (image.assignment.startPositionX ?? 0) ===
          largestStart.startPositionX &&
        (image.assignment.startPositionY ?? 0) === largestStart.startPositionY
      );
    })
    .map((image) => ({
      attachmentId: image.attachmentId,
      transform: computeStackedLayoutImageTransform({
        isLargest: image.attachmentId === largest.attachmentId,
        dims: image.dims,
        largestDims: largest.dims,
        largestStart,
        scale,
        rotation
      })
    }));
}
